import { MicrosoftEmailClient, NylasEmailClient } from 'pages/dashboard/pages/dispatcher/pages/dispatcher-emails/controllers'
import { EmailAccount } from 'pages/dashboard/pages/dispatcher/pages/dispatcher-emails/controllers/types'
import { pinOAuthAccount } from './oauthUrl'

type ShowToastFunction = (message: string, type: 'success' | 'danger') => void

export const handleMicrosoftEmailAdd = async (
    onSuccess?: (accounts: EmailAccount[]) => void,
    onError?: (error: string) => void,
    showToast?: ShowToastFunction,
    loginHint?: string
) => {
    try {
        // Get auth URL from backend (redirect URI is resolved server-side from env vars)
        const redirectUrl = window.location.origin + '/auth/microsoft/callback'
        const { authUrl } = await MicrosoftEmailClient.getAuthUrl(redirectUrl, 'APP_NUMEO_AI')

        // Read-scope reconnect: pin to the known mailbox so the signed-in user skips the chooser.
        const authWindow = window.open(pinOAuthAccount(authUrl, loginHint), 'Microsoft Auth', 'width=500,height=700,scrollbars=yes')

        if (!authWindow) {
            showToast?.('Popup was blocked. Please allow popups for this site.', 'danger')
            onError?.('Popup blocked')
            return
        }

        let authFlowCompleted = false
        let callbackInvoked = false

        const invokeErrorCallback = (errorMessage: string) => {
            if (callbackInvoked) return
            callbackInvoked = true
            if (errorMessage) {
                showToast?.(errorMessage, 'danger')
            }
            onError?.(errorMessage)
        }

        const invokeSuccessCallback = (accounts: EmailAccount[]) => {
            if (callbackInvoked) return
            callbackInvoked = true
            onSuccess?.(accounts)
        }

        const popupCheckInterval = setInterval(() => {
            if (authWindow?.closed && !authFlowCompleted && !callbackInvoked) {
                authFlowCompleted = true
                clearInterval(popupCheckInterval)
                window.removeEventListener('message', handleAuthMessage)
                invokeErrorCallback('')
            }
        }, 500)

        function handleAuthMessage(event: MessageEvent) {
            try {
                if (event.origin !== window.location.origin) return

                if (event.data.type === 'MICROSOFT_AUTH_SUCCESS') {
                    authFlowCompleted = true
                    clearInterval(popupCheckInterval)
                    window.removeEventListener('message', handleAuthMessage)
                    authWindow?.close()
                    NylasEmailClient.getAccounts()
                        .then(({ accounts }) => {
                            invokeSuccessCallback(accounts)
                        })
                        .catch((error) => {
                            console.error('Error fetching accounts after Microsoft auth:', error)
                            invokeErrorCallback('Email connected but failed to fetch accounts. Please refresh the page.')
                        })
                } else if (event.data.type === 'MICROSOFT_AUTH_ERROR') {
                    authFlowCompleted = true
                    clearInterval(popupCheckInterval)
                    window.removeEventListener('message', handleAuthMessage)
                    authWindow?.close()
                    const errorMessage = event.data.error || 'Failed to add Microsoft account'
                    invokeErrorCallback(errorMessage)
                }
            } catch (error) {
                console.error('Error processing Microsoft auth message:', error)
                authFlowCompleted = true
                clearInterval(popupCheckInterval)
                window.removeEventListener('message', handleAuthMessage)
                invokeErrorCallback('Failed to add Microsoft account')
            }
        }

        window.addEventListener('message', handleAuthMessage)
    } catch (error: unknown) {
        console.error('Error adding Microsoft email:', error)
        const errorMessage = (error as Error).message || 'Failed to add Microsoft account'
        showToast?.(errorMessage, 'danger')
        onError?.(errorMessage)
    }
}
